import React, { useState } from 'react';
import { useQuran } from '../context/QuranContext';
import { ChevronDown, Search, Book } from 'lucide-react';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from './ui/select';
import { Input } from './ui/input';
import { ScrollArea } from './ui/scroll-area';

const SurahSelector = () => {
    const { surahs, currentSurah, setCurrentSurah, currentVerse, setCurrentVerse } = useQuran();
    const [searchTerm, setSearchTerm] = useState('');
    const [isOpen, setIsOpen] = useState(false);

    const filteredSurahs = surahs.filter(surah => 
        surah.name_simple.toLowerCase().includes(searchTerm.toLowerCase()) ||
        surah.name_arabic.includes(searchTerm) ||
        surah.id.toString() === searchTerm
    );

    const handleSurahChange = (surahId) => {
        const surah = surahs.find(s => s.id === parseInt(surahId));
        if (surah) {
            setCurrentSurah(surah);
            setCurrentVerse(1);
        }
    };

    const verseOptions = currentSurah 
        ? Array.from({ length: currentSurah.verses_count }, (_, i) => i + 1)
        : [];

    return (
        <div className="flex flex-row gap-2 sm:gap-4 items-center" data-testid="surah-selector-container">
            {/* Surah Selector - Compact */}
            <div className="flex-1 sm:w-64">
                <Select 
                    value={currentSurah?.id?.toString() || ''} 
                    onValueChange={handleSurahChange}
                >
                    <SelectTrigger 
                        className="w-full h-10 bg-[#09090b]/80 border-[#1e293b] text-[#f8fafc] hover:border-[#d4af37] transition-colors text-sm"
                        data-testid="surah-select-trigger"
                    >
                        <div className="flex items-center gap-2">
                            <Book className="w-4 h-4 text-[#d4af37] flex-shrink-0" />
                            <SelectValue placeholder="Surah">
                                {currentSurah && (
                                    <span className="flex items-center gap-1 truncate">
                                        <span className="text-[#d4af37] font-medium">{currentSurah.id}.</span>
                                        <span className="truncate">{currentSurah.name_simple}</span>
                                    </span>
                                )}
                            </SelectValue>
                        </div>
                    </SelectTrigger>
                    <SelectContent 
                        className="bg-[#09090b] border-[#1e293b] max-h-[50vh]"
                        data-testid="surah-select-content"
                    >
                        <div className="p-2 sticky top-0 bg-[#09090b] border-b border-[#1e293b] z-10">
                            <div className="relative">
                                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-[#475569]" />
                                <Input
                                    placeholder="Search..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="pl-10 h-9 bg-[#0f172a] border-[#1e293b] text-[#f8fafc] placeholder:text-[#475569] text-sm"
                                    data-testid="surah-search-input"
                                />
                            </div>
                        </div>
                        <ScrollArea className="h-[40vh]">
                            {filteredSurahs.map((surah) => (
                                <SelectItem 
                                    key={surah.id} 
                                    value={surah.id.toString()}
                                    className="py-2 px-3 hover:bg-[#1e293b] cursor-pointer focus:bg-[#1e293b] focus:text-[#f8fafc]"
                                    data-testid={`surah-option-${surah.id}`}
                                >
                                    <div className="flex items-center justify-between w-full gap-2">
                                        <div className="flex items-center gap-2">
                                            <span className="text-[#d4af37] font-medium w-6 text-sm">{surah.id}</span>
                                            <span className="text-[#f8fafc] text-sm">{surah.name_simple}</span>
                                        </div>
                                        <span className="text-xs text-[#475569]">{surah.verses_count}</span>
                                    </div>
                                </SelectItem>
                            ))}
                        </ScrollArea>
                    </SelectContent>
                </Select>
            </div>

            {/* Verse Selector - Compact */}
            <div className="w-24 sm:w-32">
                <Select 
                    value={currentVerse?.toString() || '1'} 
                    onValueChange={(v) => setCurrentVerse(parseInt(v))}
                >
                    <SelectTrigger 
                        className="w-full h-10 bg-[#09090b]/80 border-[#1e293b] text-[#f8fafc] hover:border-[#d4af37] transition-colors text-sm"
                        data-testid="verse-select-trigger"
                    >
                        <SelectValue placeholder="Verse">
                            {currentVerse && (
                                <span className="text-[#d4af37] font-medium">Ayah {currentVerse}</span>
                            )}
                        </SelectValue>
                    </SelectTrigger>
                    <SelectContent 
                        className="bg-[#09090b] border-[#1e293b] max-h-[50vh]"
                        data-testid="verse-select-content"
                    >
                        <ScrollArea className="h-[40vh]">
                            {verseOptions.map((verse) => (
                                <SelectItem 
                                    key={verse} 
                                    value={verse.toString()}
                                    className="py-2 hover:bg-[#1e293b] cursor-pointer focus:bg-[#1e293b] focus:text-[#f8fafc]"
                                    data-testid={`verse-option-${verse}`}
                                >
                                    <span className="text-[#f8fafc] text-sm">Ayah {verse}</span>
                                </SelectItem>
                            ))}
                        </ScrollArea>
                    </SelectContent>
                </Select>
            </div>
        </div>
    );
};

export default SurahSelector;
